import { useState } from 'react';
import { Building2, Users, ArrowRight, Video, Check, X, Calendar, Sparkles, Send } from 'lucide-react';
import { Meta } from '../components/Meta';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export function CorporateWellness() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: '',
    team_size: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const { error } = await supabase.from('corporate_messages').insert([formData]);

      if (error) throw error;

      toast.success('Message sent! We\'ll be in touch soon.');
      setFormData({
        name: '',
        email: '',
        company_name: '',
        phone: '',
        team_size: '',
        message: ''
      });
      setShowForm(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToContact = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return (
    <div className="min-h-screen">
      <Meta 
        title="Corporate Wellness Programs | Mindful Family"
        description="Move beyond static apps. Give your team real, live connection with the only corporate wellness platform powered by live-streamed interaction and verified human experts."
      />
      
      {/* Section 1: The Hook */}
      <div className="relative bg-gradient-to-b from-[#F3F7EE] to-white py-24">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            <div className="text-left">
              <h1 className="text-4xl lg:text-5xl font-gelica font-bold text-content leading-tight mb-6">
              Corporate Wellness Programs for Healthy Teams
              </h1>
              <p className="text-xl text-content/70 mb-8">
                We help you combat employee isolation and boost engagement through live, expert-led sessions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-start items-start">
                <button
                  onClick={scrollToContact}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-accent-text hover:bg-accent-text/90 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                <a 
                  href="#pricing"
                  className="inline-flex items-center px-6 py-3 border-2 border-accent-text text-base font-medium rounded-full text-accent-text bg-transparent hover:bg-accent-text/5 transition-all duration-200 whitespace-nowrap"
                >
                  View Corporate Plans
                </a>
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <img 
                src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/listing-images/59bed50f-5ccf-4265-87fa-7743af34d361/business-wellness.png"
                alt="Corporate Wellness"
                className="blob-shape w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Trust Bar Section */}
      <div className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-8">
            <p className="text-sm font-medium text-content/60 uppercase tracking-wide mb-6">
              Trusted by forward-thinking companies
            </p>
            <div className="bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl p-8 border border-accent-text/10">
              <p className="text-lg text-content/80 italic mb-2">
                "Mindful Family brought our remote team together in a way Slack never could."
              </p>
              <p className="text-sm text-content/60">
                — Beta Partner, Tech Company
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: The Problem/Solution - Visual Comparison */}
      <div className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-gelica font-bold text-content mb-6">
              Most Wellness Benefits Go Unused Because They Are Lonely. We Fix That.
            </h2>
            <p className="text-xl text-content/70 max-w-3xl mx-auto">
              While other platforms offer isolated, pre-recorded content, we create <strong>real-time connections</strong> and <strong>vibrant communities</strong> that transform wellness from a solo journey into a shared experience.
            </p>
          </div>

          {/* Comparison Table */}
          <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-100 overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
              {/* Header Row */}
              <div className="bg-gray-50 p-6 border-b-2 border-gray-200">
                <h3 className="text-lg font-bold text-content">Feature</h3>
              </div>
              <div className="bg-red-50 p-6 border-b-2 border-red-200">
                <h3 className="text-lg font-bold text-red-700">Standard Apps</h3>
                <p className="text-sm text-red-600 mt-1">(Headspace, Calm, etc.)</p>
              </div>
              <div className="bg-green-50 p-6 border-b-2 border-green-200">
                <h3 className="text-lg font-bold text-green-700">Mindful Family</h3>
                <p className="text-sm text-green-600 mt-1">Live & Interactive</p>
              </div>

              {/* Row 1: Content Type */}
              <div className="p-6 border-b border-gray-100">
                <p className="font-medium text-content">Content Type</p>
              </div>
              <div className="p-6 border-b border-gray-100 bg-red-50/30">
                <div className="flex items-center">
                  <X className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-content">Pre-recorded content</p>
                </div>
              </div>
              <div className="p-6 border-b border-gray-100 bg-green-50/30">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-content">Daily Live Streams</p>
                </div>
              </div>
              
              {/* Row 2: Interaction */}
              <div className="p-6 border-b border-gray-100">
                <p className="font-medium text-content">Interaction</p>
                  </div>
              <div className="p-6 border-b border-gray-100 bg-red-50/30">
                <div className="flex items-center">
                  <X className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-content">Passive watching</p>
                </div>
              </div>
              <div className="p-6 border-b border-gray-100 bg-green-50/30">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-content">Interactive Q&A with Experts</p>
                  </div>
                </div>
                
              {/* Row 3: Experience */}
              <div className="p-6 border-b border-gray-100">
                <p className="font-medium text-content">Experience</p>
                  </div>
              <div className="p-6 border-b border-gray-100 bg-red-50/30">
                <div className="flex items-center">
                  <X className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-content">Solo journey</p>
                </div>
              </div>
              <div className="p-6 border-b border-gray-100 bg-green-50/30">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-content">Team Challenges & Community</p>
                </div>
              </div>

              {/* Row 4: Engagement */}
              <div className="p-6 border-b border-gray-100">
                <p className="font-medium text-content">Long-term Engagement</p>
              </div>
              <div className="p-6 border-b border-gray-100 bg-red-50/30">
                <div className="flex items-center">
                  <X className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-content">10% usage after 30 days</p>
                </div>
              </div>
              <div className="p-6 border-b border-gray-100 bg-green-50/30">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-content">High retention through community connection</p>
                  </div>
                </div>
                
              {/* Row 5: Personalization */}
              <div className="p-6">
                <p className="font-medium text-content">Personalization</p>
                  </div>
              <div className="p-6 bg-red-50/30">
                <div className="flex items-center">
                  <X className="h-5 w-5 text-red-600 mr-2" />
                  <p className="text-content">One-size-fits-all</p>
                </div>
                  </div>
              <div className="p-6 bg-green-50/30">
                <div className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-content">Real-time adaptability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: What You Get */}
      <div className="py-24 bg-white">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-gelica font-bold text-content mb-6">
              What You Get
            </h2>
            <p className="text-xl text-content/70 max-w-3xl mx-auto">
              Comprehensive wellness benefits that go beyond static apps
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {/* Unlimited Live Access */}
            <div className="bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <Video className="h-12 w-12 text-accent-text mb-6" />
              <h3 className="text-2xl font-bold text-content mb-4">
                Unlimited Live Access
              </h3>
              <p className="text-content/70 mb-6">
                Daily live streams covering Yoga, Pilates, Meditation, and Nutrition with real-time interaction.
              </p>
              <ul className="space-y-3 text-content/70">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Weekly multiple wellness livestreams</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Unlimited access to wellness courses</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Interactive Q&A sessions</span>
                </li>
              </ul>
            </div>

            {/* Verified Experts */}
            <div className="bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <Users className="h-12 w-12 text-accent-text mb-6" />
              <h3 className="text-2xl font-bold text-content mb-4">
                Verified Experts
              </h3>
              <p className="text-content/70 mb-6">
                Real humans your team can talk to, not AI bots. Direct connection with verified wellness practitioners.
              </p>
              <ul className="space-y-3 text-content/70">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Expert-moderated discussions</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Personalized guidance during sessions</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Real-time feedback and support</span>
                </li>
              </ul>
            </div>

            {/* Sustainability & Wellbeing */}
            <div className="bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <Sparkles className="h-12 w-12 text-accent-text mb-6" />
              <h3 className="text-2xl font-bold text-content mb-4">
                Sustainability & Wellbeing
              </h3>
              <p className="text-content/70 mb-6">
                Tools that encourage nature connection and sustainable living, aligning with your company's ESG and employee wellbeing goals.
              </p>
              <ul className="space-y-3 text-content/70">
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>ESG-aligned sustainability tools</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Nature connection and environmental wellness</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Access to supportive wellness community</span>
                </li>
                <li className="flex items-center">
                  <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                  <span>Exclusive discounts on retreats and events</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Transparent Pricing */}
      <div className="py-24 bg-gradient-to-br from-accent-text/5 to-accent-text/10" id="pricing">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-gelica font-bold text-content mb-4">
              Transparent Pricing
            </h2>
            <p className="text-xl text-content/70 max-w-3xl mx-auto">
              Purchase memberships in bulk for your employees and save significantly. The more memberships you buy, the lower the price per employee.
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-xl border-2 border-accent-text/20">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-6 bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl border border-accent-text/20">
                <div className="text-3xl font-bold text-accent-text mb-2">10-49</div>
                <div className="text-sm text-content/60 mb-4">Employees</div>
                <div className="text-2xl font-bold text-content mb-1">£4.50</div>
                <div className="text-sm text-content/60">per employee/month</div>
                <div className="mt-4 text-xs text-green-600 font-medium">Save 10%</div>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-accent-text to-accent-text/90 rounded-xl border-2 border-accent-text shadow-lg transform scale-105 relative">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-accent-text text-white px-3 py-1 rounded-full text-xs font-medium">
                    Best Value
                  </span>
                </div>
                <div className="text-3xl font-bold text-white mb-2">50-99</div>
                <div className="text-sm text-white/80 mb-4">Employees</div>
                <div className="text-2xl font-bold text-white mb-1">£4.00</div>
                <div className="text-sm text-white/80">per employee/month</div>
                <div className="mt-4 text-xs text-white font-medium">Save 20%</div>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl border border-accent-text/20">
                <div className="text-3xl font-bold text-accent-text mb-2">100+</div>
                <div className="text-sm text-content/60 mb-4">Employees</div>
                <div className="text-2xl font-bold text-content mb-1">Custom</div>
                <div className="text-sm text-content/60">pricing</div>
                <div className="mt-4 text-xs text-green-600 font-medium">Contact Us</div>
              </div>
            </div>

            {/* Value Gap Comparison */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-200 mb-6">
              <div className="text-center">
                <p className="text-lg font-bold text-content mb-2">
                  💰 Value Comparison
                </p>
                <p className="text-content/80">
                  <span className="line-through text-red-600">Typical cost of one workshop per employee: £250+</span>
                  <br />
                  <span className="text-xl font-bold text-green-700">Mindful Family cost: £4.50/month</span>
                </p>
                <p className="text-sm text-content/60 mt-2">
                  That's less than the cost of a single workshop, for unlimited monthly access.
                </p>
              </div>
            </div>

            <div className="bg-accent-text/5 rounded-xl p-6 border border-accent-text/20">
              <p className="text-center text-content/80 mb-4">
                <strong className="text-content">Individual membership:</strong> £5/month per person
              </p>
              <p className="text-center text-sm text-content/60">
                All bulk memberships include full premium access: unlimited live sessions, courses, holistic living tools, community access, and more.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 5: Custom Events (The Upsell) */}
      <div className="py-24 bg-white">
        <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
            <div className="bg-gradient-to-br from-accent-text/5 to-accent-text/10 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <Building2 className="h-12 w-12 text-accent-text mb-6" />
              <h3 className="text-2xl font-bold text-content mb-4">
              Need Something Specific? Let's Host Your Next Wellness Day
              </h3>
              <p className="text-content/70 mb-6">
              Whether it's an on-site retreat or a virtual team-building workshop, our experts curate bespoke experiences for your company culture.
              </p>
              <ul className="space-y-3 text-content/70 mb-6">
                <li className="flex items-center">
                <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <span>On-site or virtual wellness sessions and workshops</span>
                </li>
                <li className="flex items-center">
                <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <span>Team building activities focused on health and wellbeing</span>
                </li>
                <li className="flex items-center">
                <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <span>Expert-led stress management and mindfulness sessions</span>
                </li>
                <li className="flex items-center">
                <Check className="h-5 w-5 text-green-600 mr-3 flex-shrink-0" />
                <span>Customised wellness challenges and group activities</span>
                </li>
              </ul>
              <div className="bg-white/50 rounded-lg p-4 border border-accent-text/10">
                <p className="text-sm text-content/80 font-medium">
                  Ideal for team retreats, wellness weeks, or special company events that bring employees together around health and wellbeing.
                </p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 6: The Final CTA */}
      <div className="py-24 bg-gradient-to-br from-accent-text/3 to-accent-text/6" id="contact">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-12 border border-accent-text/10 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent-text/5 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent-text/5 rounded-full translate-y-12 -translate-x-12"></div>
            
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center px-4 py-2 bg-accent-text/10 rounded-full mb-6">
                <span className="text-sm font-medium text-accent-text">READY TO TRANSFORM YOUR WORKPLACE?</span>
              </div>
              
              <h2 className="text-3xl lg:text-4xl font-gelica font-bold text-content mb-6">
                Ready to Transform Your Workplace Culture?
              </h2>
              
              <p className="text-xl text-content/70 mb-8 max-w-2xl mx-auto leading-relaxed">
                Join the companies switching from static apps to live community.
              </p>
              
              {!showForm ? (
                <>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                    <button
                      onClick={scrollToContact}
                      className="inline-flex items-center px-8 py-4 bg-accent-text text-white text-lg font-medium rounded-full hover:bg-accent-text/90 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 whitespace-nowrap"
                    >
                      <Calendar className="mr-2 h-5 w-5" />
                      Get in Touch
                    </button>
                  </div>

                  <div className="flex items-center justify-center text-content/60 mb-6">
                    <span className="text-sm">Or</span>
                    <button
                      onClick={scrollToContact}
                      className="ml-2 text-accent-text font-medium hover:text-accent-text/80 transition-colors"
                    >
                      send us a message
                    </button>
                  </div>
                </>
              ) : (
                <form onSubmit={handleSubmit} className="max-w-md mx-auto text-left">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                        placeholder="you@company.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                        placeholder="Your company"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Team Size
                      </label>
                      <select
                        name="team_size"
                        value={formData.team_size}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                      >
                        <option value="">Select team size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                        placeholder="Tell us about your workplace wellness needs..."
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setShowForm(false)}
                        className="flex-1 px-4 py-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 px-4 py-3 bg-accent-text text-white rounded-lg text-sm font-medium hover:bg-accent-text/90 disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {submitting ? 'Sending...' : (
                          <>
                            Send Message
                            <Send className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
              
              <div className="mt-8 pt-6 border-t border-accent-text/10">
                <p className="text-sm text-content/60">
                  <span className="font-medium text-content">Free consultation</span> • No commitment required • Custom solutions for your team
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}